import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../lib/api";
import { formatMoney } from "../lib/money";
import { FEE_ESTADO_LABEL, organizerFeesApi, shortDate, type FeeLedger } from "../lib/organizerFeesApi";
import "./organizerFees.css";

/** Finanzas › «Fees de Torneos123»: lo que el organizador le debe a
 * Torneos123 por inscripciones cobradas por fuera (maqueta Fees-Deuda). */
export default function OrganizerFeesCard() {
  const { token } = useAuth();
  const [data, setData] = useState<FeeLedger | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const response = await organizerFeesApi.mine(token);
      setData(response.data);
      setError(null);
    } catch (err) {
      setData(null);
      if (err instanceof ApiError && err.status >= 500) setError(err.message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  // «Pagar ahora» (aviso al publicar) llega con #fees: se lleva la vista aquí.
  useEffect(() => {
    if (data && typeof window !== "undefined" && window.location.hash === "#fees") {
      document.getElementById("fees")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  }, [data]);

  const returned =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("resultado") : null;

  async function pay(tournamentId: number | null) {
    if (!token) return;
    const key = tournamentId === null ? "all" : String(tournamentId);
    setPaying(key);
    setError(null);
    try {
      const response = await organizerFeesApi.checkout(token, tournamentId);
      window.location.assign(response.data.checkout_url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo abrir Mercado Pago.");
      setPaying(null);
    }
  }

  // Sin datos (aún cargando o falló la lectura) no se muestra nada: Finanzas
  // sigue funcionando igual.
  if (!data) return null;
  // Sin movimientos ni saldo: no estorba en Finanzas.
  if (data.tournaments.length === 0 && Number(data.saldo) === 0 && !data.control.blocked) return null;

  const saldo = Number(data?.saldo ?? 0);
  const overdueRows = (data?.tournaments ?? []).filter((row) => row.estado === "vencido");
  const payEnabled = Boolean(data?.pay_enabled);

  return (
    <section className="card ofees" id="fees" aria-labelledby="ofees-title" data-fees-card>
      <div className="ofees__head">
        <div>
          <h2 className="ofees__title" id="ofees-title">Fees de Torneos123</h2>
          <p className="ofees__muted">
            Lo que le debes a Torneos123 por las inscripciones que cobraste tú (efectivo, transferencia a tu cuenta o
            marcadas «Pagado» a mano).
          </p>
        </div>
      </div>
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {returned === "exito" || returned === "pendiente" ? (
        <p className="form-message" role="status">
          Recibimos tu pago en Mercado Pago. Tu saldo baja en cuanto Mercado Pago lo confirme (normalmente en minutos).
        </p>
      ) : null}
      {data ? (
        <>
          <div className="ofees__kpis">
            <div className="ofees__kpi">
              <small>Debes por inscripciones cobradas por fuera</small>
              <strong>{formatMoney(data.owed, data.currency)}</strong>
            </div>
            <div className="ofees__kpi">
              <small>Pagado a Torneos123</small>
              <strong>{formatMoney(Number(data.paid) + Number(data.adjustments), data.currency)}</strong>
            </div>
            <div className="ofees__kpi ofees__kpi--navy">
              <small>Saldo</small>
              <strong data-fees-saldo>{formatMoney(Math.max(saldo, 0), data.currency)}</strong>
            </div>
          </div>
          <p className="ofees__note">
            Las inscripciones cobradas con Mercado Pago ya descuentan el fee automáticamente: no aparecen aquí.
            {saldo < 0 ? ` Tienes ${formatMoney(-saldo, data.currency)} a favor.` : ""}
          </p>
          {data.control.blocked ? (
            <p className="ofees__danger" role="alert">
              Torneos123 pausó la creación y publicación de torneos de tu cuenta por fees pendientes. Paga tu saldo para
              reactivarla.
            </p>
          ) : null}
          {overdueRows.length > 0 ? (
            <p className="ofees__warn" role="status" data-fees-overdue>
              {overdueRows.length === 1
                ? `${overdueRows[0].tournament_name} venció el ${shortDate(overdueRows[0].due_at)} (${data.due_days} días después de cerrar).`
                : `${overdueRows.length} torneos tienen fees vencidos (${formatMoney(data.saldo_overdue, data.currency)}).`}{" "}
              Mientras tengas saldo de torneos cerrados no podrás publicar un torneo nuevo.
            </p>
          ) : Number(data.saldo_closed) > 0 ? (
            <p className="ofees__warn" role="status">
              Tienes {formatMoney(data.saldo_closed, data.currency)} de torneos cerrados. Págalo dentro de los{" "}
              {data.due_days} días después de cerrar; mientras tanto no podrás publicar un torneo nuevo.
            </p>
          ) : null}
          {saldo > 0 ? (
            <div className="ofees__bar">
              <button
                type="button"
                className="ofees__btn ofees__btn--lime"
                onClick={() => void pay(null)}
                disabled={!payEnabled || paying !== null}
                data-fees-pay-all
              >
                {paying === "all" ? "Abriendo Mercado Pago…" : `Pagar ${formatMoney(saldo, data.currency)} con Mercado Pago`}
              </button>
              {!payEnabled ? <span className="ofees__note">El pago en línea se activa pronto.</span> : null}
            </div>
          ) : null}
          {data.tournaments.length > 0 ? (
            <div className="ofees__table-wrap">
              <table className="ofees__table">
                <thead>
                  <tr>
                    <th>Torneo</th>
                    <th>Estado del torneo</th>
                    <th>Inscripciones por fuera</th>
                    <th>Fee por inscripción</th>
                    <th>Total</th>
                    <th>Pagado</th>
                    <th>Saldo</th>
                    <th>Estado</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {data.tournaments.map((row) => (
                    <tr key={`${row.tournament_id ?? row.tournament_name}`}>
                      <td className="ofees__name">{row.tournament_name}</td>
                      <td data-label="Estado del torneo">
                        <span>{row.closed ? `Cerrado ${shortDate(row.closed_at)}` : "En curso"}</span>
                      </td>
                      <td data-label="Inscripciones por fuera">
                        <span>
                          {row.orders_charged}
                          {row.becas_pending + row.becas_approved > 0
                            ? ` · ${row.becas_pending + row.becas_approved} beca${row.becas_pending + row.becas_approved === 1 ? "" : "s"}`
                            : ""}
                        </span>
                      </td>
                      <td data-label="Fee por inscripción"><span>{formatMoney(row.fee_amount, data.currency)}</span></td>
                      <td data-label="Total"><span className="ofees__amt">{formatMoney(row.total, data.currency)}</span></td>
                      <td data-label="Pagado"><span>{formatMoney(row.paid, data.currency)}</span></td>
                      <td data-label="Saldo"><span className="ofees__amt">{formatMoney(row.saldo, data.currency)}</span></td>
                      <td data-label="Estado">
                        <span className={`ofees__chip ofees__chip--${row.estado}`}>{FEE_ESTADO_LABEL[row.estado]}</span>
                      </td>
                      <td data-label="">
                        {row.closed && Number(row.saldo) > 0 && row.tournament_id !== null && payEnabled ? (
                          <button
                            type="button"
                            className="ofees__btn ofees__btn--sm"
                            onClick={() => void pay(row.tournament_id)}
                            disabled={paying !== null}
                          >
                            {paying === String(row.tournament_id) ? "Abriendo…" : "Pagar este"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {data.tournaments.some((row) => !row.closed) ? (
            <p className="ofees__note">El torneo en curso todavía no se cobra: el cargo queda fijo al cerrar el torneo.</p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
